import * as React from 'react';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Timeline, Spin } from 'antd';
import styled from 'styled-components';

import { useProcessingLog } from 'core/hooks';
import type { ZoomProcessingLogFieldsFragment } from 'core/graphql';
import { formatWarsaw } from 'core/utils/zoomTime';
import { levelColor, logKey, parseDetails } from 'core/utils/zoomProcessingLog';
import { Colors } from 'services/ColorService';

interface IProcessingLogTimelineProps {
  lessonId: string;
  level?: string;
  step?: string;
  /** Poll interval in ms — pass `0` (default) to disable polling. */
  pollInterval?: number;
}

const LogItem = memo(
  ({ entry }: { entry: ZoomProcessingLogFieldsFragment }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const details = parseDetails(entry.details);
    const detailKeys = Object.keys(details);

    return (
      <Item>
        <ItemHead>
          <Step>
            {t(`pages.zoom.processingLog.steps.${logKey(entry.step)}`)}
          </Step>
          <Time>{formatWarsaw(entry.createdAt)}</Time>
        </ItemHead>
        <Message level={logKey(entry.level)}>{entry.message}</Message>
        {detailKeys.length ? (
          <>
            <Toggle type="button" onClick={() => setExpanded((v) => !v)}>
              {expanded
                ? t('pages.zoom.processingLog.hideDetails')
                : t('pages.zoom.processingLog.details')}
            </Toggle>
            {expanded ? (
              <Details>
                {detailKeys.map((key) => (
                  <DetailRow key={key}>
                    <DetailKey>{key}</DetailKey>
                    <DetailValue>{String(details[key])}</DetailValue>
                  </DetailRow>
                ))}
              </Details>
            ) : null}
          </>
        ) : null}
      </Item>
    );
  },
);

export const ProcessingLogTimeline = memo(
  ({
    lessonId,
    level,
    step,
    pollInterval = 0,
  }: IProcessingLogTimelineProps) => {
    const { t } = useTranslation();
    const { entries, loading } = useProcessingLog(lessonId, {
      level,
      step,
      pollInterval,
    });

    if (loading && !entries.length) {
      return (
        <Centered>
          <Spin />
        </Centered>
      );
    }

    if (!entries.length) {
      return <Empty>{t('pages.zoom.processingLog.empty')}</Empty>;
    }

    return (
      <Timeline>
        {entries.map((entry) => (
          <Timeline.Item key={entry.id} color={levelColor(entry.level)}>
            <LogItem entry={entry} />
          </Timeline.Item>
        ))}
      </Timeline>
    );
  },
);

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const Empty = styled.div`
  color: ${Colors.Gray};
  font-size: 13px;
  padding: 8px 0;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const Step = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.Black};
`;

const Time = styled.span`
  font-size: 12px;
  color: ${Colors.Gray};
`;

const Message = styled.div<{ level: string }>`
  font-size: 13px;
  color: ${({ level }) =>
    level === 'error' ? Colors.ErrorDefault : Colors.Black};
`;

const Toggle = styled.button`
  align-self: flex-start;
  margin-top: 2px;
  padding: 0;
  border: none;
  background: none;
  color: ${Colors.Blue};
  font-size: 12px;
  cursor: pointer;
`;

const Details = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: ${Colors.MainBackground};
  border-radius: 6px;
  padding: 8px 10px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
`;

const DetailKey = styled.span`
  color: ${Colors.Gray};
  min-width: 110px;
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  color: ${Colors.Black};
  word-break: break-all;
`;
